import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import {
  setActiveFilter,
  toggleStageFilter,
  toggleStatusFilter,
  toggleSubStatusFilter,
  clearStageSelections,
  clearStatusSelections,
  clearSubStatusSelections,
} from "../stores/notificationSlice";
import { FILTER_LABELS, type FilterCategory } from "../types";
import usePipeline from "@/features/stage/api/usePipeline";

const FILTER_ORDER: FilterCategory[] = [
  "all",
  "stage",
  "status",
  "sub_status",
  "notes",
  "chat",
  "call_history",
  "followup",
  "installments",
  "attachments",
];

function MultiSelectDropdown({
  label,
  options,
  selected,
  onToggle,
  onShowAll,
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (val: string) => void;
  onShowAll: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, minWidth: 220 });

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.toLowerCase().includes(q));
  }, [options, search]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  useEffect(() => {
    if (open) {
      // Autofocus the search input once the portal mounts.
      const t = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const padding = 8;
    let left = rect.left;
    const minW = Math.max(rect.width, 220);
    if (left + minW > vw - padding) {
      left = Math.max(padding, vw - minW - padding);
    }
    setMenuPos({
      top: rect.bottom + 4,
      left,
      minWidth: minW,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useLayoutEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updatePosition]);

  const close = () => setOpen(false);

  const dropdownContent =
    open &&
    typeof document !== "undefined" &&
    createPortal(
      <>
        <div className="fixed inset-0 z-[10000]" aria-hidden onClick={close} />
        <div
          role="listbox"
          className="fixed z-[10001] bg-white border border-[var(--color-black-1)] rounded-lg shadow-xl py-1 max-h-[240px] overflow-y-auto"
          style={{
            top: menuPos.top,
            left: menuPos.left,
            minWidth: menuPos.minWidth,
          }}
        >
          <div
            className="px-2 py-1.5 border-b border-[var(--color-black-1)] sticky top-0 bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3 text-[var(--color-text-gray)]" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}…`}
                className="w-full pl-6 pr-2 py-1 text-[11px] border border-[var(--color-black-1)] rounded outline-none focus:border-[var(--color-primary)]"
              />
            </div>
          </div>
          <button
            type="button"
            className="w-full px-2.5 py-1.5 text-left text-[11px] font-semibold text-[var(--color-primary)] hover:bg-[var(--color-green-1)] border-b border-[var(--color-black-1)] mb-0.5"
            onClick={(e) => {
              e.stopPropagation();
              onShowAll();
              close();
            }}
          >
            Show all
          </button>
          {filteredOptions.length === 0 ? (
            <div className="px-2.5 py-3 text-center text-[11px] text-[var(--color-text-gray)]">
              {options.length === 0 ? "No options available" : "No matches"}
            </div>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = selected.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  className={cn(
                    "w-full flex items-center gap-2 px-2.5 py-1.5 text-[11px] text-left hover:bg-[#f7f9f8] transition-colors",
                    isSelected && "text-[var(--color-primary)] font-medium"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(opt);
                  }}
                >
                  <span
                    className={cn(
                      "size-3.5 rounded border flex items-center justify-center shrink-0",
                      isSelected
                        ? "bg-[var(--color-primary)] border-[var(--color-primary)]"
                        : "border-[var(--color-black-2)]"
                    )}
                  >
                    {isSelected && <Check className="size-2.5 text-white" />}
                  </span>
                  {opt}
                </button>
              );
            })
          )}
        </div>
      </>,
      document.body
    );

  return (
    <div className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        className={cn(
          "inline-flex items-center gap-0.5 px-2 py-1 rounded-md text-[10px] font-medium transition-colors whitespace-nowrap",
          selected.length > 0
            ? "bg-[var(--color-primary)] text-white"
            : "bg-[var(--color-green-1)] text-[var(--color-primary)] border border-[var(--color-green-2)]"
        )}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        {selected.length > 0 && (
          <span className="bg-white/25 text-white px-1 rounded text-[9px] ml-0.5">
            {selected.length}
          </span>
        )}
        <ChevronDown
          className={cn("size-3 transition-transform", open && "rotate-180")}
        />
      </button>
      {dropdownContent}
    </div>
  );
}

export default function NotificationFilters() {
  const dispatch = useAppDispatch();
  const {
    activeFilter,
    selectedStages,
    selectedStatuses,
    selectedSubStatuses,
  } = useAppSelector((state) => state.notifications);

  // Derive filter options from tenant pipeline data. We use `usePipeline`
  // (NOT `useStages`) because the full-pipeline endpoint nests statuses +
  // subStatuses inside each stage; the plain stage list endpoint does not,
  // which is why Status / Sub Status previously came back empty.
  const { pipeline } = usePipeline();
  const stages = useMemo(() => (pipeline ?? []) as any[], [pipeline]);

  const stageOptions = useMemo(() => stages.map((s) => s.name), [stages]);

  // Status options are scoped to the currently-selected stages. When no
  // stage is selected, show every status across the pipeline so the user
  // can still filter by status alone.
  const statusOptions = useMemo(() => {
    const source =
      selectedStages.length > 0
        ? stages.filter((s) => selectedStages.includes(s.name))
        : stages;
    return [
      ...new Set(
        source.flatMap((s: any) =>
          (s.statuses ?? []).map((st: any) => st.name)
        )
      ),
    ];
  }, [stages, selectedStages]);

  // Sub-status options follow Stage AND Status selection. With nothing
  // selected, list every sub-status; with stages selected, restrict to
  // their statuses; with statuses also selected, restrict further.
  const subStatusOptions = useMemo(() => {
    const stageSource =
      selectedStages.length > 0
        ? stages.filter((s) => selectedStages.includes(s.name))
        : stages;
    const allStatuses = stageSource.flatMap((s: any) => s.statuses ?? []);
    const statusSource =
      selectedStatuses.length > 0
        ? allStatuses.filter((st: any) => selectedStatuses.includes(st.name))
        : allStatuses;
    return [
      ...new Set(
        statusSource.flatMap((st: any) =>
          (st.subStatuses ?? []).map((ss: any) => ss.name)
        )
      ),
    ];
  }, [stages, selectedStages, selectedStatuses]);

  return (
    <div className="relative z-[60] flex items-center gap-1 px-3 py-2 overflow-x-auto border-b border-[var(--color-black-1)] bg-white scrollbar-none">
      {FILTER_ORDER.map((filter) => {
        if (filter === "stage") {
          return (
            <MultiSelectDropdown
              key={filter}
              label={FILTER_LABELS[filter]}
              options={stageOptions}
              selected={activeFilter === "stage" ? selectedStages : []}
              onToggle={(val) => {
                if (activeFilter !== "stage")
                  dispatch(setActiveFilter("stage"));
                dispatch(toggleStageFilter(val));
              }}
              onShowAll={() => {
                if (activeFilter !== "stage")
                  dispatch(setActiveFilter("stage"));
                dispatch(clearStageSelections());
              }}
            />
          );
        }
        if (filter === "status") {
          return (
            <MultiSelectDropdown
              key={filter}
              label={FILTER_LABELS[filter]}
              options={statusOptions}
              selected={activeFilter === "status" ? selectedStatuses : []}
              onToggle={(val) => {
                if (activeFilter !== "status")
                  dispatch(setActiveFilter("status"));
                dispatch(toggleStatusFilter(val));
              }}
              onShowAll={() => {
                if (activeFilter !== "status")
                  dispatch(setActiveFilter("status"));
                dispatch(clearStatusSelections());
              }}
            />
          );
        }
        if (filter === "sub_status") {
          return (
            <MultiSelectDropdown
              key={filter}
              label={FILTER_LABELS[filter]}
              options={subStatusOptions}
              selected={
                activeFilter === "sub_status" ? selectedSubStatuses : []
              }
              onToggle={(val) => {
                if (activeFilter !== "sub_status")
                  dispatch(setActiveFilter("sub_status"));
                dispatch(toggleSubStatusFilter(val));
              }}
              onShowAll={() => {
                if (activeFilter !== "sub_status")
                  dispatch(setActiveFilter("sub_status"));
                dispatch(clearSubStatusSelections());
              }}
            />
          );
        }

        return (
          <button
            key={filter}
            type="button"
            className={cn(
              "px-2 py-1 rounded-md text-[10px] font-medium transition-colors whitespace-nowrap shrink-0",
              activeFilter === filter
                ? "bg-[var(--color-primary)] text-white"
                : "bg-[var(--color-green-1)] text-[var(--color-black-7)] border border-[var(--color-green-2)] hover:bg-[var(--color-green-2)]"
            )}
            onClick={() => dispatch(setActiveFilter(filter))}
          >
            {FILTER_LABELS[filter]}
          </button>
        );
      })}
    </div>
  );
}
